import { useRef } from "react";
import PropTypes from "prop-types";
import { LuMonitor, LuMoon, LuSun } from "react-icons/lu";
import { useTheme } from "../../contexts/theme-context";

const OPTIONS = [
  { value: "system", label: "System", Icon: LuMonitor },
  { value: "light", label: "Light", Icon: LuSun },
  { value: "dark", label: "Dark", Icon: LuMoon },
];

/** Segmented System / Light / Dark control. `showLabels` adds text next to each icon. */
export default function ThemeSwitch({ showLabels = false, className = "" }) {
  const { theme, setTheme } = useTheme();
  const refs = useRef([]);

  const onKeyDown = (event, index) => {
    const step = { ArrowRight: 1, ArrowDown: 1, ArrowLeft: -1, ArrowUp: -1 }[event.key];
    if (!step) return;
    event.preventDefault();
    const next = (index + step + OPTIONS.length) % OPTIONS.length;
    setTheme(OPTIONS[next].value);
    refs.current[next]?.focus();
  };

  return (
    <div role="radiogroup" aria-label="Theme" className={`inline-flex gap-0.5 rounded-xl bg-raised/70 p-1 ${className}`}>
      {OPTIONS.map(({ value, label, Icon }, index) => {
        const selected = theme === value;
        return (
          <button
            key={value}
            ref={(el) => (refs.current[index] = el)}
            type="button"
            role="radio"
            aria-checked={selected}
            aria-label={showLabels ? undefined : `${label} theme`}
            title={showLabels ? undefined : label}
            tabIndex={selected ? 0 : -1}
            onClick={() => setTheme(value)}
            onKeyDown={(event) => onKeyDown(event, index)}
            className={`inline-flex h-8 items-center justify-center gap-2 rounded-lg text-sm font-medium transition-all duration-150 ${
              showLabels ? "px-3" : "w-9"
            } ${
              selected
                ? "bg-panel text-ink shadow-sm ring-1 ring-line"
                : "text-muted hover:text-ink"
            }`}
          >
            <Icon className="h-4 w-4" aria-hidden="true" />
            {showLabels && <span>{label}</span>}
          </button>
        );
      })}
    </div>
  );
}

ThemeSwitch.propTypes = {
  showLabels: PropTypes.bool,
  className: PropTypes.string,
};
